import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useSnackbar } from 'notistack';
import { useHistory, useParams } from 'react-router';
import { createStyles, makeStyles, Theme } from '@material-ui/core/styles';
import * as Yup from 'yup';
import {
    activateCourier,
    confirmCourier,
    CourierDetailsDto,
    rejectCourier,
    CourierDataForm,
} from '../../../api/couriers';
import { DictionaryDto, useContentApi, useDictionaryApi } from '../../../api/dictionary';
import { useLoadBadge } from '../../../hooks/useLoadBadge';
import { photoText } from '../../../constants';

interface Params {
    id: string;
}

const useStyles = makeStyles((theme: Theme) =>
    createStyles({
        root: {
            flexGrow: 1,
            minHeight: '500px',
            position: 'relative',
            top: -theme.spacing(3),
            left: theme.spacing(1),
            paddingBottom: theme.spacing(3),
        },
        buttons: {
            marginTop: theme.spacing(2),
            marginBottom: theme.spacing(3),
            '& button': {
                marginRight: theme.spacing(2),
                '&:first-child': {
                    marginRight: 0,
                },
            },
        },
        tabPane: {
            flexGrow: 1,
        },
        progress: {
            height: '4px',
            width: '100%',
        },
        mt2: {
            marginTop: theme.spacing(3),
        },
    })
);

export function useCourierLogic(activation: boolean) {
    const classes = useStyles();
    const [loading, setLoading] = useState(true);
    const { enqueueSnackbar } = useSnackbar();
    const [confirmLoading, setConfirmLoading] = useState(false);
    const [request, setRequest] = useState<CourierDetailsDto>();
    const params = useParams<Params>();
    const history = useHistory();
    const [photos, setPhotos] = useState<{ id: number; desc?: string }[]>([]);
    const [instructed, setInstructed] = useState(false);
    const [personnelOpen, setPersonnelOpen] = useState(false);
    const [documentTypes, setDocumentTypes] = useState<DictionaryDto[]>([]);
    const [courierDataOpen, setCourierDataOpen] = useState(false);
    const [personnelForm] = useState({ personnelNumber: '' });
    const [courierFormOpen, setCourierFormOpen] = useState(false);
    const courierForm = useRef<CourierDataForm>({
        document_type_id: '',
        passport_number: '',
        passport_date: null,
        passport_issued_by: '',
    });
    const { loadBadges } = useLoadBadge();
    const { fetchAll: fetchDocumentTypes } = useContentApi<DictionaryDto>('/document-type/list');

    const personnelFormValidScheme = Yup.object().shape({
        personnelNumber: Yup.string().required('Введите табельный номер курьера'),
    });

    const courierDataFormValidScheme = Yup.object().shape({
        document_type_id: Yup.string().required('Выберите вид документа'),
        passport_number: Yup.string().required('Введите номер документа'),
        passport_date: Yup.string().required('Введите дату выдачи').nullable(),
        passport_issued_by: Yup.string().required('Введите кем выдано'),
    });

    const handleBack = useCallback(() => {
        history.goBack();
    }, [history]);

    const handleOpenDialog = useCallback(() => {
        if (activation) {
            setPersonnelOpen(true);
        } else {
            setCourierDataOpen(true);
        }
    }, [activation]);

    const handleCloseCourierForm = () => {
        setCourierFormOpen(false);
    };

    const handleConfirm = useCallback(
        async (data: CourierDataForm, setSubmitting: (s: boolean) => void) => {
            setConfirmLoading(true);
            try {
                await confirmCourier({ ...data, user_id: params.id });
                await loadBadges();
                handleBack();
                enqueueSnackbar('Курьер успешно подтвержден', { variant: 'success' });
            } catch (e) {
                enqueueSnackbar(`Ошибка при сохранении данных ${e.response?.data?.message || e}`, {
                    variant: 'error',
                });
            } finally {
                setConfirmLoading(false);
                setSubmitting(false);
            }
        },
        [enqueueSnackbar, handleBack, loadBadges, params.id]
    );

    const handleActivate = useCallback(
        async (personnelNumber: string, setSubmitting: (s: boolean) => void) => {
            setConfirmLoading(true);
            try {
                await activateCourier(params.id, personnelNumber);
                setPersonnelOpen(false);
                enqueueSnackbar('Курьер успешно активирован', {
                    variant: 'success',
                });
                await loadBadges();
                handleBack();
            } catch (e) {
                enqueueSnackbar(`Ошибка при сохранении данных ${e.response?.data?.message || e}`, {
                    variant: 'error',
                });
            } finally {
                setConfirmLoading(false);
                setSubmitting(false);
                setPersonnelOpen(false);
            }
        },
        [enqueueSnackbar, handleBack, loadBadges, params.id]
    );

    const handleReject = useCallback(async () => {
        setConfirmLoading(true);
        try {
            await rejectCourier(params.id);
            handleBack();
            enqueueSnackbar('Успешно отклонено', { variant: 'success' });
        } catch (e) {
            enqueueSnackbar(`Ошибка при сохранении данных ${e.response?.data?.message || e}`, {
                variant: 'error',
            });
        } finally {
            setConfirmLoading(false);
        }
    }, [enqueueSnackbar, handleBack, params.id]);

    const handleInstructed = (event: React.ChangeEvent<HTMLInputElement>) => {
        setInstructed(event.target.checked);
    };

    const { getById } = useDictionaryApi<CourierDetailsDto>('/courier/detail');

    const load = useCallback(async () => {
        try {
            setLoading(true);
            const res = await getById(params.id);
            setRequest(res);
            const photoList = res.courier_assets_group.photos.map((p) => ({
                id: p.asset_id,
                desc: photoText.get(p.type),
            }));
            setPhotos(photoList);
            setLoading(false);
        } catch (e) {
            enqueueSnackbar(`Ошибка при получении данных ${e.response?.data?.message || e}`, {
                variant: 'error',
            });
        } finally {
            setLoading(false);
        }
    }, [enqueueSnackbar, getById, params.id]);

    const loadDocumentTypes = useCallback(async () => {
        try {
            setDocumentTypes(await fetchDocumentTypes());
        } catch (e) {
            enqueueSnackbar(`Ошибка при получении данных ${e.response?.data?.message || e}`, {
                variant: 'error',
            });
        }
    }, [enqueueSnackbar, fetchDocumentTypes]);

    useEffect(() => {
        load();
    }, [load]);

    useEffect(() => {
        if (courierDataOpen && documentTypes.length === 0) {
            loadDocumentTypes();
        }
    }, [courierDataOpen, documentTypes.length, loadDocumentTypes]);

    return {
        loading,
        load,
        confirmLoading,
        handleOpenDialog,
        handleReject,
        handleInstructed,
        handleBack,
        photos,
        instructed,
        request,
        personnelForm,
        personnelOpen,
        setConfirmLoading,
        params,
        setPersonnelOpen,
        classes,
        enqueueSnackbar,
        courierDataOpen,
        setCourierDataOpen,
        handleConfirm,
        handleActivate,
        courierForm,
        documentTypes,
        personnelFormValidScheme,
        courierDataFormValidScheme,
        courierFormOpen,
        setCourierFormOpen,
        handleCloseCourierForm,
    };
}
